/**
 * DECLARED deploy-time SELECTIONS — the reader, the gate and the wire shape.
 *
 * The structural twin of `deployInputSources.js`, for the OTHER destination.
 * They are DELIBERATELY two channels (founder ruling, 2026-08-22) and merging
 * them would make one of the two lie about where its value goes:
 *
 *   deployInput  →  body.defaultInput  →  the per-RUN trigger payload
 *   selections   →  body.selections    →  the agent's encrypted ENV bag,
 *                                         configuration a deterministic node
 *                                         reads from process.env every run
 *
 * Only the CONTAINER is shared: both are collected in the same second-step
 * modal, because both are "what does this agent still need before it can run".
 *
 * THE WIRE SHAPE IS KEYED BY SELECTION ID, NEVER BY ENV KEY:
 *
 *     body.selections = { board: { provider: 'vikunja', value: '6' } }
 *                         ^^^^^ the id the TEMPLATE declared
 *
 * The client never names an env key; the backend derives it from
 * `entry.selections[].keys[provider]`. That is what makes "an undeclared key is
 * rejected" true BY CONSTRUCTION rather than by an allowlist someone has to
 * remember to apply, and it is why this file has no idea what an env key is.
 *
 * PER PROVIDER THE VALUE IS A DIFFERENT KIND OF ADDRESS — Vikunja's numeric
 * project id, Jira's project KEY ("KAN"). The normalisation happens server-side
 * in the selectables feed, so everything here is generic: an `{id, label}` list
 * and the id of whichever one was picked.
 */

/**
 * The selections a marketplace row declares, shape-guarded.
 *
 * MIRRORS `backend/src/services/deploy-selections.js declaredSelections` — a
 * row written before the field existed carries no `selections` at all, and a
 * stale one can carry anything. Both must read as "declares nothing" so the
 * modal renders no section and the deploy body is byte-identical to before.
 */
export function collectSelections(entry) {
  const raw = Array.isArray(entry?.selections) ? entry.selections : [];
  return raw.filter((s) => (
    s && typeof s === 'object'
    && typeof s.id === 'string' && s.id
    && Array.isArray(s.providers) && s.providers.length > 0
    && s.keys && typeof s.keys === 'object' && !Array.isArray(s.keys)
  ));
}

/**
 * Display name for a provider id.
 *
 * Prefers the REAL name off the integration-status payload the detail modal
 * already fetched (`status.integrations[id].name`) — the same source the
 * self-host dashboard reads. There is deliberately NO local id→name table here:
 * a second hand-kept copy of the registry's names is exactly the drift this
 * codebase keeps paying for. When no meta is available (the project-wizard
 * path, which never fetches status) the id is title-cased as a last resort,
 * the same "fall back to the id" posture self-host's selectionProviderName has.
 */
export function providerLabel(id, integrationsMeta) {
  const name = integrationsMeta && integrationsMeta[id] && integrationsMeta[id].name;
  if (typeof name === 'string' && name) return name;
  return typeof id === 'string' && id ? id.charAt(0).toUpperCase() + id.slice(1) : '';
}

/**
 * Which of a selection's declared providers the operator can actually answer
 * with, given what the section discovered by asking the selectables feed.
 *
 * `availability` is `{ [selectionId]: ['jira', …] }` — providers whose list
 * came back readable. A provider that is not connected, or whose list could not
 * be read, is NOT here: in both cases there is no honest control to draw.
 */
export function availableProviders(selection, availability) {
  const found = (availability && availability[selection.id]) || [];
  return selection.providers.filter((p) => found.includes(p));
}

/**
 * Required selections the operator has not answered — the deploy gate.
 * Returns `{ ok, errors }` keyed by selection id, mirroring
 * `validateDeployInputs`.
 *
 * A selection with NO available provider is deliberately NOT an error: the
 * required-integration gate on the detail modal already refuses that deploy,
 * and two blocks for one cause reads as two problems. Same rule as self-host's
 * `missingSelectionLabels`.
 */
export function validateSelections(selections, answers, availability) {
  const errors = {};
  for (const sel of selections) {
    if (sel.required === false) continue;
    if (availableProviders(sel, availability).length === 0) continue;
    const a = answers?.[sel.id];
    const empty = !a || typeof a.value !== 'string' || a.value.trim() === '';
    if (empty) errors[sel.id] = 'Required';
  }
  return { ok: Object.keys(errors).length === 0, errors };
}

/**
 * Build `body.selections`. Returns undefined when nothing was answered, so a
 * template that declares none — or one whose providers are all unconnected —
 * sends no `selections` key at all and the deploy stays byte-identical.
 */
export function buildSelections(selections, answers) {
  const out = {};
  for (const sel of selections) {
    const a = answers?.[sel.id];
    if (!a || typeof a.value !== 'string' || !a.value.trim()) continue;
    const provider = typeof a.provider === 'string' && a.provider
      ? a.provider
      : (sel.providers.length === 1 ? sel.providers[0] : '');
    if (!provider || !sel.providers.includes(provider)) continue;
    out[sel.id] = { provider, value: a.value.trim() };
  }
  return Object.keys(out).length > 0 ? out : undefined;
}
